"use client";
import React from "react";
import { usePathname, useRouter } from "next/navigation";

export default function AgeGate({ viewer }: { viewer: { isBanned: boolean; ageVerified: boolean; user: any; isAdmin?: boolean; banReason?: string | null } }) {
  const pathname = usePathname();
  const router = useRouter();

  const allowPaths = ["/age-verification"];
  const isAllowed = allowPaths.some((p) => pathname?.startsWith(p));
  const isAdminVerificationPage = pathname?.startsWith("/age-verification/admin") && viewer.user && viewer.isAdmin;
  // Poll when logged in and either not age verified, on the age-verification flow,
  // or when the user is currently banned so we can detect an unban.
  const shouldRefreshViewer = Boolean(
    viewer.user && (!viewer.ageVerified || pathname?.startsWith("/age-verification") || viewer.isBanned)
  );

  React.useEffect(() => {
    if (!shouldRefreshViewer) return;
    // Prefer SSE for immediate notifications from the backend. If EventSource
    // fails or is unavailable, the existing polling remains as a fallback.
    let es: EventSource | null = null;
    if (typeof window !== "undefined" && "EventSource" in window) {
      try {
        es = new EventSource("/api/realtime/sse");
        es.onmessage = (ev) => {
          try {
            const payload = JSON.parse(ev.data);
            if (payload?.type === "account_banned" && !viewer.isBanned) {
              // Force a full page load so the server-rendered layout re-evaluates
              // `getViewer()` and displays the `BannedNotice` immediately.
              try {
                window.location.replace("/");
              } catch {
                // fallback to router navigation if window is unavailable
                router.replace("/");
              }
            }
            if (payload?.type === "account_unbanned" && viewer.isBanned) {
              router.refresh();
            }
          } catch {
            // ignore malformed messages
          }
        };
      } catch {
        es = null;
      }
    }

    const refreshViewer = async () => {
      try {
        const res = await fetch("/api/auth/me", { cache: "no-store" });
        if (!res.ok) return;

        const nextViewer = await res.json();

        // If viewer is newly banned, navigate to root so the server-rendered
        // layout can show the BannedNotice. If viewer was banned but is now
        // unbanned, refresh the app to re-render server components with the
        // updated viewer state.
        if (nextViewer.isBanned && !viewer.isBanned) {
          router.replace("/");
          return;
        }

        if (!nextViewer.isBanned && viewer.isBanned) {
          router.refresh();
          return;
        }

        if (nextViewer.ageVerified && pathname?.startsWith("/age-verification") && !isAdminVerificationPage) {
          router.replace("/");
          return;
        }

        if (!nextViewer.ageVerified && !isAllowed) {
          router.replace("/age-verification");
        }
      } catch {
        // ignore transient fetch issues; the app will keep the current view
      }
    };

    void refreshViewer();
    const id = window.setInterval(() => {
      void refreshViewer();
    }, 7000);

    return () => {
      window.clearInterval(id);
      try {
        es?.close();
      } catch {}
    };
  }, [viewer.user, viewer.isBanned, viewer.ageVerified, pathname, isAllowed, isAdminVerificationPage, shouldRefreshViewer, router]);

  if (!viewer.user) return null;
  // Banned UI is handled centrally in the root layout via `BannedNotice`.
  // This component should not render its own banned view to avoid duplication.

  if (viewer.ageVerified && pathname?.startsWith("/age-verification") && !isAdminVerificationPage) {
    return null;
  }

  if (!viewer.ageVerified && !isAllowed) {
    return null;
  }

  return null;
}
